import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCelebration } from '@/context/CelebrationContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { Calendar, ExternalLink, Users, Star, Lock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toStringArray } from '@/lib/utils';

const API_URL = window.location.origin;

const SessionsPage = () => {
  const { user, hasTier } = useAuth();
  const { grandCelebrate } = useCelebration();
  const [coaches, setCoaches] = useState([]);
  const [founders, setFounders] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [matchedCoach, setMatchedCoach] = useState(null);

  // Check if user is Inner Edge only (must use assigned coach)
  const isInnerEdgeOnly = hasTier('inner_edge') && !hasTier('sdc') && !hasTier('coaching_activation');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [walletRes, bookingsRes, foundersRes] = await Promise.all([
        axios.get(`${API_URL}/api/sessions/wallet`),
        axios.get(`${API_URL}/api/sessions/bookings`),
        axios.get(`${API_URL}/api/coaches/founders`)
      ]);
      
      setWallet(walletRes.data);
      setBookings(bookingsRes.data);
      setFounders(foundersRes.data);
      setSelectedCoach(walletRes.data.selected_coach_id);

      // Fetch appropriate coaches based on tier
      if (hasTier('coaching_activation') || hasTier('sdc')) {
        const coachesRes = await axios.get(`${API_URL}/api/coaches`);
        setCoaches(coachesRes.data);
      } else if (isInnerEdgeOnly) {
        // Inner Edge only: fetch ONLY the matched coach from diagnostic results
        try {
          const diagnosticRes = await axios.get(`${API_URL}/api/diagnostic/results`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const coachData = diagnosticRes.data?.coach_match?.coach;
          if (coachData) {
            setMatchedCoach(coachData);
            setCoaches([coachData]);
          }
        } catch (err) {
          console.error('Failed to fetch matched coach:', err);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookCoachSession = async () => {
    if (!selectedCoach) {
      toast.error('No coach assigned yet — the Sister Diamonds team will assign your coach');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/sessions/book/coach`);
      grandCelebrate('confetti', 'sessionBooked');
      toast.success('Investing in yourself? That\'s power!');
      if (response.data.calendly_link) {
        window.open(response.data.calendly_link, '_blank');
      }
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book session');
    }
  };

  const handleBookFounderSession = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/sessions/book/founder`);
      grandCelebrate('sparkle', 'sessionBooked');
      toast.success('A Wonder Twin session awaits you!');
      window.open(response.data.calendly_link, '_blank');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book session');
    }
  };

  const handleBookInnerEdgeSession = async (coachId) => {
    try {
      const response = await axios.post(`${API_URL}/api/sessions/book/inner-edge?coach_id=${coachId}`);
      grandCelebrate('confetti', 'sessionBooked');
      toast.success('Your transformation is scheduled!');
      window.open(response.data.calendly_link, '_blank');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book session');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="sessions-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-forest">Sacred Sessions</h1>
          <p className="text-gray-600 mt-2">
            Investing in yourself through coaching is one of the most powerful things you can do. 
            Book time with your diverse, vetted guides.
          </p>
        </div>

        {/* Session Wallet */}
        {wallet && (
          <Card className="card-base mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest">Your Session Wallet</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {hasTier('coaching_activation') && (
                  <>
                    <div className="text-center p-6 bg-forest/5 rounded-xl">
                      <p className="text-4xl font-bold text-forest">{wallet.coach_sessions_remaining}</p>
                      <p className="text-gray-600 mt-1">Coach Sessions Remaining</p>
                      <Button 
                        className="mt-4 btn-primary" 
                        onClick={handleBookCoachSession}
                        disabled={wallet.coach_sessions_remaining <= 0 || !selectedCoach}
                      >
                        Book Coach Session
                      </Button>
                    </div>
                    <div className="text-center p-6 bg-terracotta/5 rounded-xl">
                      <p className="text-4xl font-bold text-terracotta">{wallet.founder_sessions_remaining}</p>
                      <p className="text-gray-600 mt-1">Wonder Twin Sessions</p>
                      <Button 
                        className="mt-4 btn-secondary" 
                        onClick={handleBookFounderSession}
                        disabled={wallet.founder_sessions_remaining <= 0}
                      >
                        Book Wonder Twin
                      </Button>
                    </div>
                  </>
                )}
                {hasTier('inner_edge') && (
                  <div className="text-center p-6 bg-forest/5 rounded-xl">
                    <p className="text-4xl font-bold text-forest">{wallet.inner_edge_sessions_remaining}</p>
                    <p className="text-gray-600 mt-1">Inner Edge Sessions</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Your Coach (Signature Coaching) - assigned by admin */}
        {hasTier('coaching_activation') && (
          <Card className="card-base mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest">Your Coach</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-32 bg-sand rounded-xl animate-pulse" />
              ) : (() => {
                const assignedCoach = coaches.find((c) => c.id === selectedCoach);
                return assignedCoach ? (
                  <div 
                    className="bg-gradient-to-r from-forest/5 to-terracotta/5 rounded-xl p-6 border-2 border-forest/20"
                    data-testid="assigned-coach-card"
                  >
                    <div className="flex flex-col md:flex-row items-center gap-6">
                      <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
                        <AvatarImage src={assignedCoach.image_url || undefined} />
                        <AvatarFallback className="bg-forest text-white text-2xl">
                          {assignedCoach.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="text-center md:text-left">
                        <Badge className="bg-terracotta/10 text-terracotta mb-2">Assigned by Sister Diamonds</Badge>
                        <h3 className="font-heading text-2xl font-bold text-forest">{assignedCoach.name}</h3>
                        <p className="text-gray-600">{assignedCoach.title}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8" data-testid="no-coach-assigned">
                    <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium">Your coach hasn't been assigned yet.</p>
                    <p className="text-sm text-gray-500 mt-1">
                      The Sister Diamonds team will match you with your coach — they'll appear here once assigned.
                    </p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Inner Edge - Your Assigned Coach */}
        {isInnerEdgeOnly && (
          <Card className="card-base mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest">Your Assigned Coach</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-32 bg-sand rounded-xl animate-pulse" />
              ) : matchedCoach ? (
                <div className="bg-gradient-to-r from-forest/5 to-terracotta/5 rounded-xl p-6 border-2 border-forest/20">
                  <div className="flex flex-col md:flex-row items-center gap-6">
                    <div className="relative">
                      <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
                        <AvatarImage src={matchedCoach.image_url || undefined} />
                        <AvatarFallback className="bg-forest text-white text-2xl">
                          {matchedCoach.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <Badge className="absolute -top-2 -right-2 bg-terracotta text-white">
                        <Star className="h-3 w-3 mr-1" /> Primary
                      </Badge>
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="font-heading text-xl font-semibold text-forest">{matchedCoach.name}</h3>
                      <p className="text-terracotta font-medium">{matchedCoach.title}</p>
                      <p className="text-gray-600 text-sm mt-2 line-clamp-2">{matchedCoach.bio}</p>
                      {(() => {
                        const specs = toStringArray(matchedCoach.specialties);
                        if (specs.length === 0) return null;
                        return (
                          <div className="flex flex-wrap gap-2 mt-3 justify-center md:justify-start">
                            {specs.slice(0, 3).map((spec, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {spec}
                              </Badge>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button 
                        className="btn-primary" 
                        onClick={() => handleBookInnerEdgeSession(matchedCoach.id)}
                        disabled={wallet?.inner_edge_sessions_remaining <= 0}
                      >
                        Book Session <Calendar className="ml-2 h-4 w-4" />
                      </Button>
                      <Link to={`/coaches/${matchedCoach.id}`}>
                        <Button variant="outline" className="w-full btn-outline">
                          View Profile
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-4">
                    This coach was matched to you based on your diagnostic results
                  </p>
                </div>
              ) : (
                <div className="text-center py-8 px-4 bg-sand/30 rounded-xl">
                  <Lock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="font-heading text-lg text-forest mb-2">Complete Your Diagnostic First</h3>
                  <p className="text-gray-600 mb-4">
                    As an Inner Edge member, you'll be matched with an ideal coach based on your unique leadership profile.
                  </p>
                  <Link to="/diagnostic">
                    <Button className="btn-primary">
                      Start Diagnostic <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Wonder Twin Sessions (Founders) */}
        {founders.length > 0 && (hasTier('coaching_activation') || hasTier('sdc')) && (
          <Card className="card-base mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest">Wonder Twin Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-forest text-white rounded-xl p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center">
                    <Users className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold">{founders[0]?.name}</h3>
                    <p className="text-white/80">{founders[0]?.title}</p>
                  </div>
                </div>
                <p className="text-white/80 mb-4">{founders[0]?.short_bio}</p>
                <Button 
                  className="bg-terracotta text-white hover:bg-terracotta/90"
                  onClick={handleBookFounderSession}
                  disabled={wallet?.founder_sessions_remaining <= 0}
                >
                  Book Wonder Twin Session <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Booking History */}
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-forest">Booking History</CardTitle>
          </CardHeader>
          <CardContent>
            {bookings.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No bookings yet. Book your first session above!</p>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <div key={booking.id} className="flex items-center justify-between p-4 bg-sand/30 rounded-xl">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-lg bg-forest flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">{booking.coach_name}</p>
                        <p className="text-sm text-gray-500">
                          {booking.session_type === 'wonder_twin' ? 'Wonder Twin Session' : 'Coaching Session'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={
                        booking.status === 'completed' ? 'bg-green-100 text-green-700' :
                        booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }>
                        {booking.status}
                      </Badge>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(booking.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SessionsPage;
